/**
 * Date utility functions for IST (Indian Standard Time) timezone handling
 */

/**
 * Ensure timestamp has 'Z' suffix for UTC designation
 * MongoDB returns timestamps without 'Z', causing browser to interpret as local time
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} Timestamp with 'Z' suffix
 */
export function ensureUTC(timestamp) {
  if (!timestamp) return null;
  if (typeof timestamp !== 'string') return timestamp;
  if (timestamp.endsWith('Z')) return timestamp;
  // Already has timezone offset (e.g., +00:00, +05:30)
  if (/[+-]\d{2}:\d{2}$/.test(timestamp)) return timestamp;
  return timestamp + 'Z';
}

/**
 * Format a UTC date/time to IST timezone
 * @param {Date|string} date - Date object or ISO string
 * @param {string} formatType - 'time' | 'date' | 'datetime'
 * @returns {string} Formatted date/time string in IST
 */
export function formatToIST(date, formatType = 'time') {
  if (!date) return 'N/A';

  // Ensure UTC designation for string timestamps
  const dateStr = typeof date === 'string' ? ensureUTC(date) : date;
  const d = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;

  // Check if date is valid
  if (isNaN(d.getTime())) return 'Invalid Date';

  const options = {
    timeZone: 'Asia/Kolkata',
    hour12: true
  };

  switch (formatType) {
    case 'time':
      options.hour = '2-digit';
      options.minute = '2-digit';
      break;
    case 'date':
      options.year = 'numeric';
      options.month = 'short';
      options.day = 'numeric';
      break;
    case 'datetime':
      options.year = 'numeric';
      options.month = 'short';
      options.day = 'numeric';
      options.hour = '2-digit';
      options.minute = '2-digit';
      break;
    default:
      options.hour = '2-digit';
      options.minute = '2-digit';
  }

  return d.toLocaleString('en-IN', options);
}

/**
 * Format time only (h:mm AM/PM) in IST
 * @param {Date|string} date
 * @returns {string}
 */
export function formatTimeIST(date) {
  return formatToIST(date, 'time');
}

/**
 * Format date only (MMM d, yyyy) in IST
 * @param {Date|string} date
 * @returns {string}
 */
export function formatDateIST(date) {
  return formatToIST(date, 'date');
}

/**
 * Format full date and time in IST
 * @param {Date|string} date
 * @returns {string}
 */
export function formatDateTimeIST(date) {
  return formatToIST(date, 'datetime');
}
